import getStudies from './studiesList';
import { DicomMetadataStore, log, utils, Enums } from '@ohif/core';
import isSeriesFilterUsed from '../../utils/isSeriesFilterUsed';

const { getSplitParam } = utils;

/**
 * Initialize the route.
 *
 * @param props.servicesManager to read services from
 * @param props.studyInstanceUIDs for a list of studies to read
 * @param props.dataSource to read the data from
 * @param props.filters filters from query params to read the data from
 * @returns array of subscriptions to cancel
 */
export async function defaultRouteInit(
  { servicesManager, studyInstanceUIDs, dataSource, filters, appConfig }: withAppTypes,
  hangingProtocolId,
  stageIndex
) {
  const { displaySetService, hangingProtocolService, uiNotificationService, customizationService } =
    servicesManager.services;
  /**
   * Function to apply the hanging protocol when the minimum number of display sets were
   * received or all display sets retrieval were completed
   * @returns
   */
  function applyHangingProtocol() {
    const displaySets = displaySetService.getActiveDisplaySets();

    if (!displaySets || !displaySets.length) {
      return;
    }

    // Keep viewport selection aligned with study-panel review order
    const sortFunctions = customizationService.getCustomization('studyBrowser.sortFunctions') as
      | { sortFunction: (a: unknown, b: unknown) => number }[]
      | undefined;
    const sortFn = sortFunctions?.[0]?.sortFunction;
    if (typeof sortFn === 'function') {
      displaySetService.sortDisplaySets(sortFn, 'ascending', true);
      utils.logDicomSortOrder?.(
        'defaultRouteInit before hanging protocol',
        displaySetService.getActiveDisplaySets()
      );
    }

    // Gets the studies list to use
    const studies = getStudies(studyInstanceUIDs, displaySetService.getActiveDisplaySets());

    // study being displayed, and is thus the "active" study.
    const activeStudy = studies[0];

    // If hangingProtocolId is 'default', pass undefined to allow automatic protocol matching
    // This enables our custom protocols (like myCtProtocol) to be automatically selected
    // based on their matching rules (e.g., Modality = CT)
    const protocolIdToUse = hangingProtocolId === 'default' ? undefined : hangingProtocolId;

    // run the hanging protocol matching on the displaySets
    // If protocolIdToUse is undefined, it will automatically match the best protocol
    hangingProtocolService.run(
      { studies, activeStudy, displaySets: displaySetService.getActiveDisplaySets() },
      protocolIdToUse,
      {
        stageIndex,
      }
    );
  }

  const unsubscriptions = [];
  const issuedWarningSeries = [];
  const { unsubscribe: instanceAddedUnsubscribe } = DicomMetadataStore.subscribe(
    DicomMetadataStore.EVENTS.INSTANCES_ADDED,
    function ({ StudyInstanceUID, SeriesInstanceUID, madeInClient = false }) {
      const seriesMetadata = DicomMetadataStore.getSeries(StudyInstanceUID, SeriesInstanceUID);

      // checks if the series filter was used, if it exists
      const seriesInstanceUIDs = filters?.seriesInstanceUID;
      if (
        seriesInstanceUIDs?.length &&
        !isSeriesFilterUsed(seriesMetadata.instances, filters) &&
        !issuedWarningSeries.includes(seriesInstanceUIDs[0])
      ) {
        // stores the series instance filter so it shows only once the warning
        issuedWarningSeries.push(seriesInstanceUIDs[0]);
        uiNotificationService.show({
          title: 'Series filter',
          message: `Each of the series in filter: ${seriesInstanceUIDs} are not part of the current study. The entire study is being displayed`,
          type: 'error',
          duration: 7000,
        });
      }

      displaySetService.makeDisplaySets(seriesMetadata.instances, { madeInClient });
    }
  );

  unsubscriptions.push(instanceAddedUnsubscribe);

  log.time(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
  log.time(Enums.TimingEnum.STUDY_TO_FIRST_IMAGE);

  const allRetrieves = studyInstanceUIDs.map(StudyInstanceUID =>
    dataSource.retrieve.series.metadata({
      StudyInstanceUID,
      filters,
      returnPromises: true,
      sortCriteria: customizationService.getCustomization('sortingCriteria'),
    })
  );

  // log the error if this fails, otherwise it's so difficult to tell what went wrong...
  allRetrieves.forEach(retrieve => {
    retrieve.catch(error => {
      console.error(error);
    });
  });

  // is displaysets from URL and has initialSOPInstanceUID or initialSeriesInstanceUID
  // then we need to wait for all display sets to be retrieved before applying the hanging protocol
  const params = new URLSearchParams(window.location.search);

  const initialSeriesInstanceUID = getSplitParam('initialseriesinstanceuid', params);
  const initialSOPInstanceUID = getSplitParam('initialsopinstanceuid', params);

  let displaySetFromUrl = false;
  if (initialSeriesInstanceUID || initialSOPInstanceUID) {
    displaySetFromUrl = true;
  }

  const loadSeriesMetadataOnDemand = appConfig?.loadSeriesMetadataOnDemand === true;
  const loadSeriesMetadataOnDemandBackgroundCount =
    Math.max(0, Number(appConfig?.loadSeriesMetadataOnDemandBackgroundCount) || 0);
  // When loadSeriesMetadataOnDemand is true, use default protocol so only one series is required for first paint
  const initialHangingProtocolId = loadSeriesMetadataOnDemand ? 'default' : hangingProtocolId;

  await Promise.allSettled(allRetrieves).then(async promises => {
    log.timeEnd(Enums.TimingEnum.STUDY_TO_DISPLAY_SETS);
    log.time(Enums.TimingEnum.DISPLAY_SETS_TO_FIRST_IMAGE);
    log.time(Enums.TimingEnum.DISPLAY_SETS_TO_ALL_IMAGES);

    const allPromises = [];
    const remainingPromises = [];

    function startRemainingPromises(remainingPromisesToStart, limitCount = 0) {
      const flat = remainingPromisesToStart.flat();
      if (limitCount > 0) {
        flat.slice(0, limitCount).forEach(pr => pr.start());
      } else {
        flat.forEach(pr => pr.start());
      }
    }

    promises.forEach(promise => {
      const retrieveSeriesMetadataPromise = promise.value;
      if (!Array.isArray(retrieveSeriesMetadataPromise)) {
        return;
      }

      if (displaySetFromUrl) {
        const requiredSeriesPromises = retrieveSeriesMetadataPromise.map(promise =>
          promise.start()
        );
        allPromises.push(Promise.allSettled(requiredSeriesPromises));
      } else {
        const protocolIdForFilter =
          initialHangingProtocolId === 'default' ? undefined : initialHangingProtocolId;
        const { requiredSeries, remaining } = hangingProtocolService.filterSeriesRequiredForRun(
          protocolIdForFilter,
          retrieveSeriesMetadataPromise
        );
        const requiredSeriesPromises = requiredSeries.map(promise => promise.start());
        allPromises.push(Promise.allSettled(requiredSeriesPromises));
        remainingPromises.push(remaining);
      }
    });

    await Promise.allSettled(allPromises).then(applyHangingProtocol);
    const backgroundCount = loadSeriesMetadataOnDemand
      ? loadSeriesMetadataOnDemandBackgroundCount
      : 0;
    startRemainingPromises(
      remainingPromises,
      backgroundCount > 0 ? backgroundCount : 0
    );
    applyHangingProtocol();
  });

  return unsubscriptions;
}
